import {
  type ReactElement,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";

import { uuidv4 } from "simplydo/core/helpers";
import { cx, styled } from "simplydo/react/styled";

import {
  FocusStackContext,
  createFocusTrap,
  useFocusStack,
} from "../utils/FocusStack";
import { Portal } from "../utils/Portal";
import { getDefaultMountNode } from "../utils/mountNode";
import { Dialog, DimmerOverlay } from "./Modal.styles";
import type { ModalDimmerProp, ModalProps } from "./Modal.types";
import { ModalContext } from "./ModalContext";

function resolveDimmerFlags(dimmer: ModalDimmerProp | undefined): {
  show: boolean;
  custom: ReactElement | null;
} {
  if (dimmer === false) {
    return { show: false, custom: null };
  }
  if (isValidElement(dimmer)) {
    return { show: true, custom: dimmer };
  }

  // true | "blurring" | undefined | plain object → show dark dimmer
  return { show: true, custom: null };
}

function stopClickPropagation(e: ReactMouseEvent<HTMLDivElement>): void {
  e.stopPropagation();
}

// oxlint-disable-next-line react/only-export-components
const ModalComponent = (props: ModalProps): ReactElement | null => {
  const {
    open: openProp,
    onClose,
    onOpen,
    size,
    fullscreen,
    basic,
    dimmer = true,
    closeOnDocumentClick = true,
    closeOnDimmerClick = true,
    closeOnEscape = true,
    className,
    style,
    children,
  } = props;

  const focusStack = useFocusStack();
  const [dialogNode, setDialogNode] = useState<HTMLDivElement | null>(null);
  const setModalRef = useCallback((node: HTMLDivElement | null) => {
    setDialogNode(node);
  }, []);
  const [internalOpen, setInternalOpen] = useState(openProp ?? false);

  const initialTitle = uuidv4();
  const [titleId] = useState(initialTitle);

  const isControlled = openProp !== undefined;
  const isOpen = isControlled ? openProp : internalOpen;

  const setOpen = useCallback(
    (next: boolean, event: Event | null) => {
      if (!isControlled) {
        setInternalOpen(next);
      }
      const data = { open: next };
      if (next) {
        onOpen?.(event as never, data);
      } else {
        onClose?.(event as never, data);
      }
    },
    [isControlled, onClose, onOpen],
  );

  const canCloseOnDimmer =
    closeOnDocumentClick !== false && closeOnDimmerClick !== false;

  // Body scroll lock
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Focus trap — Portal mounts asynchronously; wait until the dialog node exists.
  useLayoutEffect(() => {
    if (!isOpen || !dialogNode) {
      return;
    }

    const returnTo = document.activeElement as HTMLElement | null;
    const focusTrap = createFocusTrap(dialogNode);
    focusStack.insertFocusTrap(focusTrap);

    return () => {
      focusStack.removeFocusTrap(focusTrap);
      if (
        returnTo &&
        returnTo.isConnected &&
        document.activeElement !== returnTo
      ) {
        returnTo.focus();
      }
    };
  }, [isOpen, dialogNode, focusStack]);

  // Escape
  useEffect(() => {
    if (!isOpen || !closeOnEscape) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false, e);
      }
    };
    document.addEventListener("keydown", onKeyDown, { capture: true });
    return () => {
      document.removeEventListener("keydown", onKeyDown, { capture: true });
    };
  }, [isOpen, closeOnEscape, setOpen]);

  if (!isOpen) {
    return null;
  }

  const { show: showDimmer, custom } = resolveDimmerFlags(dimmer);
  const dialog = (
    <Dialog
      ref={setModalRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex={-1}
      className={cx(
        fullscreen && "is-fullscreen",
        basic && "is-basic",
        className,
      )}
      style={style}
      $size={size}
      $fullscreen={fullscreen}
      onClick={stopClickPropagation}
    >
      {children}
    </Dialog>
  );

  const handleDimmerClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) {
      return;
    }
    if (canCloseOnDimmer) {
      e.stopPropagation();
      setOpen(false, e.nativeEvent);
    }
  };

  let overlay: ReactNode;
  if (!showDimmer) {
    overlay = (
      <DimmerOverlay
        role="presentation"
        style={{ background: "transparent" }}
        onClick={handleDimmerClick}
      >
        {dialog}
      </DimmerOverlay>
    );
  } else if (custom) {
    const customProps = custom.props as {
      onClick?: (e: ReactMouseEvent<HTMLDivElement>) => void;
    };
    overlay = cloneElement(custom, {
      onClick: (e: ReactMouseEvent<HTMLDivElement>) => {
        customProps.onClick?.(e);
        handleDimmerClick(e);
      },
      children: dialog,
    } as never);
  } else {
    overlay = (
      <DimmerOverlay role="presentation" onClick={handleDimmerClick}>
        {dialog}
      </DimmerOverlay>
    );
  }
  const portalRoot = getDefaultMountNode() ?? undefined;

  return (
    <FocusStackContext.Provider value={focusStack}>
      <ModalContext.Provider value={{ titleId, basic }}>
        <Portal open={isOpen} mountNode={portalRoot}>
          {overlay}
        </Portal>
      </ModalContext.Provider>
    </FocusStackContext.Provider>
  );
};

export const Modal = styled(ModalComponent)``;
